import { useCallback, useEffect, useMemo, useState } from 'react';
import { INDIA_STATES, stateKey } from '../components/CsrDashboard/indiaStatePaths';
import { errorMessage, plural } from '../components/CsrDashboard/csrFormat';
import {
  fetchCsrStateCoverage,
  fetchDashboardMetrics,
  fetchPartnerDashboardForAdmin,
} from '../services/dashboardApi';

const coverageErrorMessage = (err) => {
  const status = err?.response?.status;
  if (status === 403) return 'The CSR Impact dashboard is available for CSR and NGO partners only.';
  if (status === 404) return 'This partner could not be found.';
  return err?.response?.data?.message || 'Could not load the CSR Impact dashboard.';
};

export const useCsrCoverage = (adminId) => {
  const [coverage, setCoverage] = useState(null);
  const [coverageStatus, setCoverageStatus] = useState('loading');
  const [coverageError, setCoverageError] = useState('');
  const [scope, setScope] = useState('all');

  const loadCoverage = useCallback(async () => {
    setCoverageStatus('loading');
    try {
      const data = await fetchCsrStateCoverage(adminId);
      setCoverage(data);
      setCoverageStatus('success');
    } catch (err) {
      setCoverageError(coverageErrorMessage(err));
      setCoverageStatus('error');
    }
  }, [adminId]);

  useEffect(() => { loadCoverage(); }, [loadCoverage]);

  const activeStates = useMemo(
    () => (coverage?.active_states || []).map((s) => ({ ...s, key: stateKey(s.state) })),
    [coverage]
  );
  const coverageByKey = useMemo(() => new Map(activeStates.map((s) => [s.key, s])), [activeStates]);
  const stateOptions = useMemo(() => {
    const known = new Set(INDIA_STATES.map((s) => stateKey(s.name)));
    const extra = activeStates.filter((s) => !known.has(s.key)).map((s) => ({ key: s.key, name: s.state }));
    return [...INDIA_STATES.map((s) => ({ key: stateKey(s.name), name: s.name })), ...extra];
  }, [activeStates]);

  const scopeRow = scope === 'all' ? null : coverageByKey.get(scope) || null;
  const isStateScope = Boolean(scopeRow);
  const isNoDataScope = scope !== 'all' && !scopeRow;
  const scopeName = scope === 'all'
    ? 'All India'
    : scopeRow?.state || stateOptions.find((o) => o.key === scope)?.name || scope;
  const eyebrow = isNoDataScope
    ? `${scopeName} · no data yet`
    : isStateScope ? scopeName : `All India · ${plural(activeStates.length, 'active state', 'active states')}`;

  return {
    coverage, coverageStatus, coverageError, loadCoverage,
    activeStates, coverageByKey, stateOptions,
    scope, setScope, scopeRow, isStateScope, isNoDataScope, scopeName, eyebrow,
    partner: coverage?.partner,
  };
};

// Existing dashboard-metrics API (partner) or its Support equivalent, filtered by state.
export const useCsrMetrics = (adminId, enabled, state) => {
  const [metrics, setMetrics] = useState(null);
  const [metricsStatus, setMetricsStatus] = useState('loading');

  useEffect(() => {
    if (!enabled) return undefined;
    let ignore = false;
    const params = state ? { state } : {};
    setMetricsStatus('loading');
    (adminId ? fetchPartnerDashboardForAdmin(adminId, params) : fetchDashboardMetrics(params))
      .then((data) => {
        if (ignore) return;
        setMetrics(Object.fromEntries((data?.metrics || []).map((m) => [m.key, m.count])));
        setMetricsStatus('success');
      })
      .catch(() => {
        if (ignore) return;
        setMetrics(null);
        setMetricsStatus('error');
      });
    return () => { ignore = true; };
  }, [adminId, enabled, state]);

  return { metrics, metricsStatus };
};

const SEARCH_DELAY_MS = 400;

// Server-side paged list with debounced search. The page resets to 1 whenever the search or
// filters change, without an extra request, and stale responses are ignored.
export const useCsrPagedList = (fetcher, adminId, filters, { limit = 15, enabled = true } = {}) => {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const filterKey = JSON.stringify(filters || {});
  const resetKey = `${query}|${filterKey}`;
  const [paging, setPaging] = useState({ key: resetKey, page: 1 });
  const page = paging.key === resetKey ? paging.page : 1;
  const [result, setResult] = useState({ status: 'loading', rows: [], total: 0, error: '' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!enabled) return undefined;
    let ignore = false;
    setResult((r) => ({ ...r, status: 'loading', error: '' }));
    fetcher(adminId, { ...JSON.parse(filterKey), search: query, page, limit })
      .then((data) => {
        if (!ignore) setResult({ status: 'success', rows: data?.rows || [], total: data?.total || 0, error: '' });
      })
      .catch((err) => {
        if (!ignore) setResult((r) => ({ ...r, status: 'error', error: errorMessage(err, 'Could not load these records.') }));
      });
    return () => { ignore = true; };
  }, [fetcher, adminId, filterKey, query, page, limit, enabled, attempt]);

  return {
    ...result,
    page,
    limit,
    query,
    search,
    setSearch,
    setPage: (p) => setPaging({ key: resetKey, page: p }),
    retry: () => setAttempt((a) => a + 1),
  };
};
